import * as THREE from 'three';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import TWEEN from '@tweenjs/tween.js';
import UIEventBus from '../UI/EventBus';
import BezierEasing from 'bezier-easing';
import { MonitorKeyframe, IdleKeyframe, LoadingKeyframe, DeskKeyframe, OrbitControlsStart, FreeLookKeyframe, DebugKeyframe, } from './CameraKeyframes';
export var CameraKey;
(function (CameraKey) {
    CameraKey["IDLE"] = "idle";
    CameraKey["MONITOR"] = "monitor";
    CameraKey["LOADING"] = "loading";
    CameraKey["DESK"] = "desk";
    CameraKey["ORBIT_CONTROLS_START"] = "orbitControlsStart";
    CameraKey["FREELOOK"] = "freeLook";
    CameraKey["DEBUG"] = "debug";
})(CameraKey || (CameraKey = {}));
export default class Camera extends EventEmitter {
    constructor() {
        super();
        this.application = new Application();
        this.sizes = this.application.sizes;
        this.scene = this.application.scene;
        this.renderer = this.application.renderer;
        this.resources = this.application.resources;
        this.time = this.application.time;
        this.position = new THREE.Vector3(0, 0, 0);
        this.focalPoint = new THREE.Vector3(0, 0, 0);
        this.freeCam = false;
        this.debugCam = false;
        this.keyframes = {
            idle: new IdleKeyframe(),
            monitor: new MonitorKeyframe(),
            loading: new LoadingKeyframe(),
            desk: new DeskKeyframe(),
            orbitControlsStart: new OrbitControlsStart(),
            freeLook: new FreeLookKeyframe(),
            debug: new DebugKeyframe(),
        };
        // document.addEventListener('mousedown', (event) => {
        //     event.preventDefault();
        //     // @ts-ignore
        //     if (event.target.id === 'prevent-click') return;
        //     // print target and current keyframe
        //     if (
        //         this.currentKeyframe === CameraKey.IDLE ||
        //         this.targetKeyframe === CameraKey.IDLE
        //     ) {
        //         this.transition(CameraKey.DESK);
        //     } else if (
        //         this.currentKeyframe === CameraKey.DESK ||
        //         this.targetKeyframe === CameraKey.DESK
        //     ) {
        //         this.transition(CameraKey.IDLE);
        //     }
        // });
        window.addEventListener('keydown', (e) => {
            if (e.key === 'p' || e.key === 'P') {
                this.toggleDebugCam();
            }
        });
        this.setPostLoadTransition();
        this.setInstance();
        this.setMonitorListeners();
        this.setFreeCamListeners();
    }
    toggleDebugCam() {
        this.debugCam = !this.debugCam;
        if (this.debugCam) {
            this.baseRequestPointerLock();
            this.transition(CameraKey.DEBUG, 0);
        }
        else {
            document.exitPointerLock();
            this.transition(CameraKey.IDLE, 1000);
        }
    }
    baseRequestPointerLock() {
        const element = this.renderer.instance.domElement;
        // @ts-ignore
        element.requestPointerLock = element.requestPointerLock || element.mozRequestPointerLock || element.webkitRequestPointerLock;
        element.requestPointerLock();
    }
    transition(key, duration = 1000, easing, callback) {
        if (this.currentKeyframe === key)
            return;
        if (this.targetKeyframe)
            TWEEN.removeAll();
        this.currentKeyframe = undefined;
        this.targetKeyframe = key;
        const keyframe = this.keyframes[key];
        const posTween = new TWEEN.Tween(this.position)
            .to(keyframe.position, duration)
            .easing(easing || TWEEN.Easing.Quintic.InOut)
            .onComplete(() => {
            this.currentKeyframe = key;
            this.targetKeyframe = undefined;
            if (callback)
                callback();
        });
        const focTween = new TWEEN.Tween(this.focalPoint)
            .to(keyframe.focalPoint, duration)
            .easing(easing || TWEEN.Easing.Quintic.InOut);
        posTween.start();
        focTween.start();
    }
    setInstance() {
        this.instance = new THREE.PerspectiveCamera(35, this.sizes.width / this.sizes.height, 10, 900000);
        this.currentKeyframe = CameraKey.LOADING;
        this.scene.add(this.instance);
    }
    setMonitorListeners() {
        this.on('enterMonitor', () => {
            // Disable freeCam when entering monitor
            this.freeCam = false;
            this.transition(CameraKey.MONITOR, 2000, BezierEasing(0.13, 0.99, 0, 1));
            UIEventBus.dispatch('enterMonitor', {});
        });
        this.on('leftMonitor', () => {
            // Reset yaw to look at screen (negative Z). Change -Math.PI / 2 to adjust rotation.
            const freeLookKeyframe = this.keyframes[CameraKey.FREELOOK];
            freeLookKeyframe.yaw = -Math.PI / 2;
            freeLookKeyframe.update();
            // Re-enable freeCam when leaving monitor
            this.transition(CameraKey.FREELOOK, 2000, BezierEasing(0.13, 0.99, 0, 1), () => {
                this.freeCam = true;
            });
            UIEventBus.dispatch('leftMonitor', {});
        });
    }
    setFreeCamListeners() {
        UIEventBus.on('freeCamToggle', (toggle) => {
            // if (toggle === this.freeCam) return;
            if (toggle) {
                this.transition(CameraKey.FREELOOK, 750, BezierEasing(0.13, 0.99, 0, 1), () => {
                    this.freeCam = true;
                    this.orbitControls.enabled = false;
                });
                // @ts-ignore
                document.getElementById('webgl').style.pointerEvents = 'none';
            }
            else {
                this.freeCam = false;
                this.transition(CameraKey.IDLE, 4000, TWEEN.Easing.Exponential.Out);
                // @ts-ignore
                document.getElementById('webgl').style.pointerEvents = 'none';
            }
        });
    }
    setPostLoadTransition() {
        UIEventBus.on('loadingScreenDone', () => {
            // 1. Start at Room View (FreeLook) INSTANTLY
            this.transition(CameraKey.FREELOOK, 0);
            // 2. Wait 1 second then zoom to Monitor
            setTimeout(() => {
                this.trigger('enterMonitor');
                // Ensure pointer events are disabled on webgl canvas so iframe can receive them
                // @ts-ignore
                document.getElementById('webgl').style.pointerEvents = 'none';
            }, 1000); // 1 second delay
        });
    }
    resize() {
        this.instance.aspect = this.sizes.width / this.sizes.height;
        this.instance.updateProjectionMatrix();
    }
    createControls() {
        this.renderer = this.application.renderer;
        this.orbitControls = new OrbitControls(this.instance, this.renderer.instance.domElement);
        const { x, y, z } = this.keyframes.orbitControlsStart.focalPoint;
        this.orbitControls.target.set(x, y, z);
        this.orbitControls.enablePan = false;
        this.orbitControls.enableDamping = true;
        this.orbitControls.object.position.copy(this.keyframes.orbitControlsStart.position);
        this.orbitControls.dampingFactor = 0.05;
        this.orbitControls.maxPolarAngle = Math.PI / 2;
        this.orbitControls.minDistance = 4000;
        this.orbitControls.maxDistance = 29000;
        this.orbitControls.enabled = false; // Disable by default
        this.orbitControls.update();
    }
    update() {
        TWEEN.update();
        if (this.freeCam) {
            const keyframe = this.keyframes[CameraKey.FREELOOK];
            keyframe.update();
            this.position.copy(keyframe.position);
            this.focalPoint.copy(keyframe.focalPoint);
        }
        for (const key in this.keyframes) {
            const _key = key;
            this.keyframes[_key].update();
        }
        if (this.currentKeyframe) {
            const keyframe = this.keyframes[this.currentKeyframe];
            this.position.copy(keyframe.position);
            this.focalPoint.copy(keyframe.focalPoint);
        }
        this.instance.position.copy(this.position);
        this.instance.lookAt(this.focalPoint);
    }
}
